import EditIcon from "@mui/icons-material/Edit";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import { setProperty } from "dot-prop";
import isEqual from "lodash.isequal";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EditButton from "../../../components/EditButton.js";
import EditableField from "../../../components/EditableField.js";
import LoadingBackdrop from "../../../components/LoadingBackdrop.js";
import { useUser } from "../../../components/UserContext.js";
import DataBox from "../../../components/data-box/DataBox.js";
import ErrorDialog from "../../../components/dialogs/ErrorDialog.js";
import Event from "../../../components/page-item/Event.js";
import SectionTitle from "../../../components/page-item/SectionTitle.js";
import {
  getCharacter,
  getCharacters,
  getHost,
  getHosts,
  getPlots,
  postCharacter,
  putCharacter,
  putHost,
} from "../../../lib/apiConfig.js";
import {
  PAGE_CONTAINER_SX,
  PAGE_TITLE_SX,
  ROUTES,
} from "../../../lib/constants.js";
import { filterVisibleCharacters } from "../../../lib/filterVisibleCharacters.js";
import { filterVisibleHosts } from "../../../lib/filterVisibleHosts.js";
import { useFetch } from "../../../lib/hooks/useFetch.js";
import CharacterInvolvements from "./CharacterInvolvements.js";
import Relations from "./Relations.js";
import AssignToHostDialog from "./components/dialogs/AssignToHostDialog.js";

const emptyCharacter = {
  name: null,
  concept: null,
  story: null,
  personality: null,
  involved: [],
  relationships: [],
};

export default function CharacterPage({ isCreating, locales }) {
  const { canEdit, canViewOtherHidden, hostEmployeeId, runId } = useUser();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [characterId, setCharacterId] = useState(null);
  const [originalCharacter, setOriginalCharacter] = useState(null);
  const [character, setCharacter] = useState(
    isCreating ? emptyCharacter : null
  );
  const [hostId, setHostId] = useState(null);
  const [host, setHost] = useState(null);
  const [plots, setPlots] = useState(null);
  const [characters, setCharacters] = useState(null);
  const [hosts, setHosts] = useState(null);
  const [newHost, setNewHost] = useState(null);
  const [isEditing, setIsEditing] = useState(isCreating);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingCharacter } = useFetch({
    endpoint: getCharacter({ id: characterId, runId }),
    mustFetch: characterId && !character,
    onSuccess: (response) => {
      setOriginalCharacter(response);
      setCharacter(response);
      setHostId(response?.host?.id);
    },
    onFail: setError,
  });

  const { isFetching: isFetchingHost } = useFetch({
    endpoint: getHost({ id: hostId, runId }),
    mustFetch: hostId && !host,
    onSuccess: setHost,
    onFail: setError,
  });

  const { fetchData: fetchPlots, isFetching: isFetchingPlots } = useFetch({
    endpoint: getPlots({ runId }),
    mustFetch: isCreating && !plots,
    onSuccess: (response) => {
      const processedPlots = response.map((plot) => ({
        id: plot.id,
        name: plot.name,
      }));

      setPlots(processedPlots);
    },
    onFail: setError,
  });

  const { fetchData: fetchCharacters, isFetching: isFetchingCharacters } =
    useFetch({
      endpoint: getCharacters({ runId }),
      mustFetch: isCreating && !characters,
      onSuccess: (response) => {
        const processedVisibleCharacters = filterVisibleCharacters({
          canViewOtherHidden,
          characters: response,
          hostEmployeeId,
        }).reduce(
          (acc, item) => (item.id !== character.id ? [...acc, item] : acc),
          []
        );

        setCharacters(processedVisibleCharacters);
      },
      onFail: setError,
    });

  const { fetchData: fetchHosts, isFetching: isFetchingHosts } = useFetch({
    endpoint: getHosts({ runId }),
    onSuccess: (response) => {
      const visibleHosts = filterVisibleHosts({
        canViewOtherHidden,
        hostEmployeeId,
        hosts: response,
      });

      setHosts(visibleHosts);
    },
    onFail: setError,
  });

  const { fetchData: saveCharacter, isFetching: isSavingCharacter } = useFetch({
    endpoint: putCharacter({ id: characterId, runId }),
    onSuccess: (response) => {
      setOriginalCharacter(response);
      setCharacter(response);
      setIsEditing(false);
    },
    onFail: setError,
  });

  const { fetchData: saveHost, isFetching: isSavingHost } = useFetch({
    endpoint: putHost({ id: newHost?.id, runId }),
    onSuccess: (host) => {
      const updatedCharacter = { ...character, host };

      setHost(host);
      setNewHost(null);
      setOriginalCharacter(updatedCharacter);
      setCharacter(updatedCharacter);
    },
    onFail: setError,
  });

  const { fetchData: createCharacter, isFetching: isCreatingCharacter } =
    useFetch({
      endpoint: postCharacter({ runId }),
      onSuccess: (response) => {
        const newCharacter = {
          ...response,
          involved: response.involved || [],
          relationships: response.relationships || [],
        };
        const newCharacterId = response.id;

        setOriginalCharacter(newCharacter);
        setCharacter(newCharacter);
        setIsEditing(false);
        navigate(
          `/${ROUTES.PLOTS.PLOTS.BASE}/${ROUTES.PLOTS.CHARACTERS.DETAIL.replace(
            ":id",
            newCharacterId
          )}`,
          { state: { id: newCharacterId } }
        );
      },
      onFail: setError,
    });

  useEffect(() => {
    if (!isCreating && state?.id !== characterId) {
      setCharacterId(state?.id);
      setCharacter(null);
      setHostId(null);
      setHost(null);
    }
  }, [characterId, isCreating, state]);

  useEffect(() => {
    newHost && saveHost({ ...newHost, character: { id: characterId } });
  }, [characterId, newHost, saveHost]);

  const plotsWithEvents = useMemo(
    () =>
      character?.involved?.reduce(
        (acc, involvement) =>
          involvement?.plot?.eventName ? [...acc, involvement?.plot] : acc,
        []
      ) || [],
    [character]
  );

  const handleEditClick = async () => {
    if (isEditing && !isCreating) {
      setCharacter({
        ...character,
        involved: character.involved.filter(
          (involvement) =>
            Boolean(involvement?.plot?.id) &&
            Boolean(involvement?.involvement?.trim())
        ),
        relationships: character.relationships.filter(
          (relation) =>
            Boolean(relation?.object?.id) &&
            Boolean(relation?.description?.trim())
        ),
      });

      isEqual(character, originalCharacter)
        ? setIsEditing(false)
        : saveCharacter(character);
    } else {
      await Promise.all([
        ...(plots ? [] : [fetchPlots()]),
        ...(characters ? [] : [fetchCharacters()]),
      ]);
      setIsEditing(true);
    }

    if (isCreating) {
      !isEqual(character, emptyCharacter) && createCharacter(character);
    }
  };

  const changeCharacterProp = ({ field, value }) => {
    setCharacter((prevCharacter) => {
      const newCharacter = { ...prevCharacter };
      setProperty(newCharacter, field, value);

      return newCharacter;
    });
  };

  const changePlot = ({ field, index, value }) => {
    setCharacter({
      ...character,
      involved: character.involved.map((involvement, i) =>
        i === index
          ? setProperty({ ...involvement }, field, value)
          : involvement
      ),
    });
  };

  const changeRelation = ({ field, index, value }) => {
    setCharacter({
      ...character,
      relationships: character.relationships.map((relationship, i) =>
        i === index
          ? setProperty({ ...relationship }, field, value)
          : relationship
      ),
    });
  };

  const deletePlot = (index) => {
    setCharacter({
      ...character,
      involved: character.involved.filter((involvement, i) => i !== index),
    });
  };

  const deleteRelation = (index) => {
    setCharacter({
      ...character,
      relationships: character.relationships.filter(
        (relationship, i) => i !== index
      ),
    });
  };

  const addPlot = () => {
    setCharacter({
      ...character,
      involved: [...character.involved, { involvement: null, plot: {} }],
    });
  };

  const addRelation = () => {
    setCharacter({
      ...character,
      relationships: [
        ...character.relationships,
        { description: null, object: {} },
      ],
    });
  };

  return (!isCreating && !character) ||
    isFetchingCharacter ||
    isFetchingHost ||
    isFetchingPlots ||
    isFetchingCharacters ||
    isFetchingHosts ||
    isSavingCharacter ||
    isSavingHost ||
    isCreatingCharacter ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_CONTAINER_SX}>
      <Box
        id="character-header"
        display="flex"
        alignItems="start"
        justifyContent="space-between"
      >
        <Box display="flex" flexDirection="column">
          <EditableField
            component="h1"
            isEditing={isEditing}
            label={locales["character.name"]}
            onChange={({ target }) =>
              changeCharacterProp({ field: "name", value: target.value })
            }
            sx={PAGE_TITLE_SX}
            text={character.name}
            value={character.name}
          />
        </Box>
        {canEdit && (
          <Box display="flex" gap={2}>
            {!isEditing && (
              <EditButton
                ariaLabel={locales["character.assign"]}
                onClick={() => {
                  fetchHosts();
                  setShowAssignDialog(true);
                }}
                primaryIcon={<SwitchAccountIcon />}
              />
            )}
            <EditButton
              ariaLabel={locales["character.edit"]}
              isEditing={isEditing}
              onClick={handleEditClick}
              primaryIcon={<EditIcon />}
              secondaryIcon={<SaveAsIcon />}
            />
          </Box>
        )}
      </Box>
      <Grid container id="character-page">
        {isCreating ? null : (
          <>
            <Grid xs={12} smd={4} md={3}>
              <DataBox locales={locales} character={character} host={host} />
            </Grid>
            <Grid xs={12} smd={1} />
          </>
        )}
        <Grid
          xs={12}
          smd={isCreating ? 12 : 7}
          md={isCreating ? 12 : 8}
          sx={{ mt: { xs: 4, smd: 0 } }}
        >
          <Box display="flex" flexDirection="column" gap={4}>
            <EditableField
              id="character-story"
              isEditing={isEditing}
              label={locales["character.story"]}
              multiline
              onChange={({ target }) =>
                changeCharacterProp({ field: "story", value: target.value })
              }
              text={character.story}
              value={character.story}
              variant="body1"
            />
            <Box id="character-personality">
              {character.personality || isEditing ? (
                <SectionTitle text={locales["character.personality"]} />
              ) : null}
              <EditableField
                id="character-personality"
                isEditing={isEditing}
                label={locales["character.personality"]}
                multiline
                onChange={({ target }) =>
                  changeCharacterProp({
                    field: "personality",
                    value: target.value,
                  })
                }
                text={character.personality}
                value={character.personality}
                variant="body1"
              />
            </Box>
            {character.involved.length || isEditing ? (
              <Box id="character-plots">
                <SectionTitle text={locales["character.plots"]} />
                {character.involved.length ? (
                  <CharacterInvolvements
                    buttonAriaLabel={
                      locales["character.plot.involvement.delete"]
                    }
                    handleChange={changePlot}
                    handleDelete={deletePlot}
                    involvements={character.involved}
                    isEditing={isEditing}
                    plotLabel={locales["character.plot.involvement"]}
                    plotNameLabel={locales["character.plot.name"]}
                    predictiveList={plots}
                  />
                ) : null}
                {(isCreating || isEditing) && (
                  <EditButton
                    ariaLabel={locales["character.plot.involvement.add"]}
                    isEditing={isEditing}
                    onClick={addPlot}
                    primaryIcon={<PlaylistAddIcon />}
                  />
                )}
              </Box>
            ) : null}
            {plotsWithEvents.length ? (
              <Box id="character-events">
                <SectionTitle text={locales["character.events"]} />
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: "repeat(1, 1fr)",
                    smd: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  }}
                  gap={4}
                >
                  {plotsWithEvents.map((plot, index) => (
                    <Event key={index} locales={locales} plot={plot} />
                  ))}
                </Box>
              </Box>
            ) : null}
            {character.relationships.length || isEditing ? (
              <Box id="character-relations">
                <SectionTitle text={locales["character.relations"]} />
                <Relations
                  currentName={character.name}
                  handleChange={changeRelation}
                  handleDelete={deleteRelation}
                  isEditing={isEditing}
                  locales={locales}
                  predictiveList={characters}
                  relations={character.relationships}
                />
                {(isCreating || isEditing) && (
                  <EditButton
                    ariaLabel={locales["character.relation.add"]}
                    isEditing={isEditing}
                    onClick={addRelation}
                    primaryIcon={<GroupAddIcon />}
                  />
                )}
              </Box>
            ) : null}
          </Box>
        </Grid>
      </Grid>
      <AssignToHostDialog
        character={character}
        hosts={hosts}
        locales={locales}
        onAssign={(host) => {
          setNewHost(host);
          setShowAssignDialog(false);
        }}
        onClose={() => setShowAssignDialog(false)}
        show={showAssignDialog}
      />
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
